import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Injectable } from "@angular/core";

import { Subject, Observable } from "rxjs";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  RatePromiseService,
  SystemConfigPromiseService,
  OrderDataPromiseLookupService,
  ProfilePromiseActionService,
  Rate,
  Temperature,
  COLUMN_ORDER_BASE_TRACKING,
  COLUMN_ORDER_NDF_BASE_TRACKING,
  CcyPromiseLookupService,
  ICcyLookupService,
  TableModelObject,
  ActiveUserProfilePromiseService,
  ActiveUser,
} from "../../../shared/cubiq-services/index";
import { BfxBlotterOrdersService } from "../../bfx-blotter-orders/bfx-blotter-orders.service";

import { LadderPair } from "../../../shared/cubiq-services/model/orders/LadderPair";
import { LadderRate } from "../../../shared/cubiq-services/model/orders/LadderRate";
import { OrderLadderDefinitionFactory } from "shared/cubiq-services/model/OrderLadderDefinitionFactory";
import { RateCache } from "shared/cubiq-services/rate/impl/RateCache";
import {
  COLUMN_INVERT_SPOT_TRACKING,
  COLUMN_INVERT_NDF_TRACKING,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { PairPositions } from "../../../shared/cubiq-services/model/orders/PairPositions";
import { OrderPosition } from "../../../shared/cubiq-services/model/orders/OrderPosition";
import {
  LadderAggregate,
  AggregateServiceContext,
} from "../../../shared/cubiq-services/model/orders/LadderAggregate";

import { LimitOrder } from "../../../shared/cubiq-services/model/orders/LimitOrder";
import { RowDataTransaction } from "ag-grid-community/dist/lib/rowModels/clientSide/clientSideRowModel";

const AGGREGATE_DELETED_STATES = new Set([
  CubiqTS.OrderState.GAMMA_MID_PENDING,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.EXPIRED,
  CubiqTS.OrderState.REJECTED,
  CubiqTS.OrderState.CANCELLED,
  CubiqTS.OrderState.CLIENT_REJECTED,
  CubiqTS.OrderState.TRADER_REJECTED,
  CubiqTS.OrderState.DELETED,
  CubiqTS.OrderState.SALES_REJECTED,
  CubiqTS.OrderState.FILLED,
  CubiqTS.OrderState.EXPIRED,
]);

@Injectable()
export class BfxLadderOrdersService extends BfxBlotterOrdersService {
  private orderPairNameMap: Map<string, string> = new Map();
  private ordersByPair: Map<string, Set<string>> = new Map();
  private ladderPairMap: Map<string, LadderPair> = new Map();
  private pairIsInvertMap: Map<string, boolean> = new Map();

  public ladderPairTrayProvider: Array<LadderPair> = [];
  private invertPairSubject: Subject<TableModelObject[]> = new Subject();
  public invertPair$: Observable<TableModelObject[]> = this.invertPairSubject.asObservable();
  public currencyBaseService: ICcyLookupService;

  batchTransactionSubject: Subject<{
    transaction: RowDataTransaction;
    callback?: () => void;
  }> = new Subject();
  hasBatchUpdateSubscription = false;
  orderIdAggregateIdMap: Map<string, string> = new Map();

  constructor(
    protected rateService: RatePromiseService,
    protected systemConfigService: SystemConfigPromiseService,
    public dataService: OrderDataPromiseLookupService,
    public tableDefinitionFactory: OrderLadderDefinitionFactory,
    protected profileActionService: ProfilePromiseActionService,
    rateCache: RateCache,
    currencyPromiseService: CcyPromiseLookupService,
    activeUserService: ActiveUserProfilePromiseService,
  ) {
    super(
      rateService,
      rateCache,
      systemConfigService,
      dataService,
      tableDefinitionFactory,
      profileActionService,
      activeUserService,
    );

    this.currencyBaseService = currencyPromiseService.getBase();
  }

  protected initData(primed: boolean): void {
    if (!primed) {
      this.rejectPriming(
        this._readyResFn,
        this._readyRejFn,
        Error(`${this.constructor.name}.dataService.isPrimed() returned false`),
      );
      return;
    }

    this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
      this.dataService.getAll().then(
        (rows: any) => {
          this.onInitialPrime(rows);
          this.flushQueuedSubjects();
          this.subscribeToSubject();
          this.resolvePriming(this._readyResFn, this._readyRejFn);
        },
        (error) => {
          this.rejectPriming(this._readyResFn, this._readyRejFn, error);
        },
      );
    });

    //this.resolvePriming(this._readyResFn, this._readyRejFn);
  }

  protected onUpdateEvent(rows: any): void {
    super.onUpdateEvent(rows);
  }

  protected processRates(rates: Array<Rate>): Array<LimitOrder> {
    let updatedRows = [];

    if (Array.isArray(rates) === false || rates.length === 0) {
      return updatedRows;
    }

    const processableRates: Array<Rate> = rates.filter(
      (rate) => CubiqTS.Tenor.SPOT == rate?.tenor || rate?.ndfHorizon,
    );
    const updated: Set<LadderRate | LadderAggregate | LimitOrder> = new Set();
    const updatedProductCodes: Set<string> = new Set();

    processableRates.forEach((rate) => {
      let pairName: string = rate.baseCurrency + "/" + rate.nonBaseCurrency;
      let ladderRate: LadderRate = this._rawData[pairName];
      let ladderPair = this._rawData[rate.baseCurrency + "/" + rate.nonBaseCurrency + "_div"];

      if (ladderRate && !ladderRate.pairIsEmpty) {
        this.updateLadderRateProperties(ladderRate);
        this._rawData[rate.baseCurrency + "/" + rate.nonBaseCurrency] = ladderRate;
        updated.add(ladderRate);
      }
      if (ladderPair) {
        ladderPair.tradable = rate.tradable;
        updated.add(ladderPair);
      }

      if (updatedProductCodes.has(rate.productCode)) return;

      let orderSet: Set<string> = this._pairOrderIds.get(rate.productCode);
      if (orderSet) {
        for (let id of orderSet) {
          let lo: LimitOrder = this._rawData[id];
          if (lo) {
            lo.tempStale = true;

            updated.add(lo);
            if (lo.aggregate != null) {
              lo.aggregate.tempStale = true;
              updated.add(lo.aggregate);
            }
          }
        }
        updatedProductCodes.add(rate.productCode);
      }
    });

    updatedRows = [...updated.values()];
    if (this.hasBatchUpdateSubscription) {
      this.batchTransactionSubject.next({
        transaction: { add: [], update: updatedRows, remove: [] },
      });
    }

    return updatedRows;
  }

  public updatePairTemperature(pairName: string): void {
    let ladderPair: LadderPair = this.ladderPairMap.get(pairName);

    if (ladderPair != null && ladderPair.currentOrderCount > 0) {
      for (let id of ladderPair.currentOrders) {
        let order = this._rawData[id];
        if (
          order &&
          (ladderPair.temperature == undefined || order.ordTemp > ladderPair.temperature)
        ) {
          ladderPair.temperature = order.ordTemp;
        }
      }

      if (this.hasBatchUpdateSubscription) {
        this.batchTransactionSubject.next({
          transaction: { add: [], update: [ladderPair], remove: [] },
        });
      }
    }
  }

  public updateCurrentFilteredPositions(
    positions: Map<string, OrderPosition>,
    callback: () => void,
  ): void {
    if (positions != null) {
      this.ladderPairMap.forEach((value: LadderPair) => {
        if (value.currentOrderPositions) value.currentOrderPositions.clear();
      });

      positions.forEach((position, key) => {
        let pair = this.orderPairNameMap.get(key);
        let ladderPair: LadderPair = this.ladderPairMap.get(pair);
        if (ladderPair) {
          if (!ladderPair.currentOrderPositions) {
            ladderPair.currentOrderPositions = new PairPositions(pair);
          }
          ladderPair.currentOrderPositions.addPosition(position);
        }
      });
      let changedOrders: Array<any> = [];
      this.ladderPairMap.forEach((value: LadderPair) => {
        changedOrders = changedOrders.concat(
          this.recalculatePositions(value.getAboveSortedPositions()),
        );
        changedOrders = changedOrders.concat(
          this.recalculatePositions(value.getBelowSortedPositions()),
        );
      });

      if (this.hasBatchUpdateSubscription && changedOrders.length > 0) {
        return this.batchTransactionSubject.next({
          transaction: {
            add: [],
            update: changedOrders,
            remove: [],
          },
          callback: callback,
        });
      }
    }

    callback?.();
  }

  private recalculatePositions(positions: Array<OrderPosition>): Array<any> {
    let changedOrders: Array<any> = [];
    let curPos: CubiqTS.BigNumber = new CubiqTS.BigNumber(0);

    if (positions) {
      positions.forEach((position) => {
        let order = this._rawData[position.orderId.toString()];
        if (order == null) return;
        if (order.isLadderAggregate && order.aggregateExpanded) {
          changedOrders.push(order);
          return;
        }

        curPos = curPos.plus(position.position);
        let newPosition: string = "";
        if (CubiqTS.PositionUnit.Oz == position.positionUnitId) {
          newPosition = curPos.toFormat(2) + " Oz";
          if (newPosition === "-0.00 Oz") {
            newPosition = "0.00 Oz";
          }
        } else {
          newPosition = curPos.dividedBy(1e6).toFormat(2) + " M";
          if (newPosition === "-0.00 M") {
            newPosition = "0.00 M";
          }
        }

        if (order && order.position != newPosition) {
          order.position = newPosition;
          changedOrders.push(order);
        }
      });
    }
    return changedOrders;
  }

  public updateCurrentFilteredOrderIds(orderIds: Set<string> | null, callback: () => void): void {
    let transaction = { add: [], update: [], remove: [] };

    if (orderIds == null) {
      this.ladderPairMap.forEach((ladderPair: LadderPair) => {
        const pairName = ladderPair.baseCode + "/" + ladderPair.nonBaseCode;
        ladderPair.clearCurrentOrders();
        for (let orderId of this.ordersByPair.get(pairName).values()) {
          ladderPair.addCurrentOrder(orderId);
        }
      });
    } else {
      this.ladderPairMap.forEach((ladderPair: LadderPair) => {
        ladderPair.clearCurrentOrders();
        ladderPair.temperature = Temperature.Cold;
      });

      orderIds.forEach((orderIdentifier) => {
        let orderId: string = orderIdentifier.toString();
        let pair = this.orderPairNameMap.get(orderId.toString());
        let ladderPair: LadderPair = this.ladderPairMap.get(pair);
        if (ladderPair) {
          ladderPair.addCurrentOrder(orderId);
          let order = this._rawData[orderId];
          if (
            order &&
            (ladderPair.temperature == undefined || order.ordTemp > ladderPair.temperature)
          ) {
            ladderPair.temperature = order.ordTemp;
          }
        }
      });
    }

    this.ladderPairMap.forEach((ladderPair: LadderPair) => {
      ladderPair.associatedRate.pairIsEmpty = ladderPair.pairIsEmpty;
      if (!ladderPair.pairIsEmpty) {
        transaction.update.push(ladderPair);
        transaction.update.push(ladderPair.associatedRate);
      }
      // updates for rates here only.
      // add/removal must be done in order transaction prior to this transaction.
    });

    if (this.hasBatchUpdateSubscription) {
      this.batchTransactionSubject.next({ transaction: transaction, callback: callback });
    } else {
      callback();
    }
  }

  protected onFullUpdateEvent(rows: Array<any>, _: boolean = false): void {
    for (let i = 0, len = rows.length; i < len; i++) {
      this.addOrUpdateRow(rows[i]);
    }
  }

  protected onDeleteEvent(rowId: string): void {
    let transaction = { add: [], update: [], remove: [] };

    let pairName: string = this.orderPairNameMap.get(rowId);
    if (pairName) {
      let orders = this.ordersByPair.get(pairName);
      if (orders) {
        orders.delete(rowId);
        if (orders.size == 0) {
          let ladderPair = this._rawData[pairName + "_div"];

          if (Object.hasOwn(this._rawData, pairName)) {
            transaction.remove.push(this._rawData[pairName]);
            delete this._rawData[pairName];
          } else {
            logger.out.error(`
                  ${this.constructor.name} missing pair ${pairName}`);
          }

          if (Object.hasOwn(this._rawData, pairName + "_div")) {
            transaction.remove.push(this._rawData[pairName + "_div"]);
            delete this._rawData[pairName + "_div"];
          } else {
            logger.out.error(`
                  ${this.constructor.name} missing pair ${pairName} divider`);
          }

          this.ladderPairMap.delete(pairName);

          if (ladderPair != null) {
            if (this.ladderPairTrayProvider) {
              try {
                for (let i = 0; i <= this.ladderPairTrayProvider.length - 1; i++) {
                  if (
                    ladderPair.baseCode == this.ladderPairTrayProvider[i].baseCode &&
                    ladderPair.nonBaseCode == this.ladderPairTrayProvider[i].nonBaseCode
                  ) {
                    this.ladderPairTrayProvider.splice(i, 1);
                    break;
                  }
                }
              } catch (e) {
                logger.out.error(
                  this.constructor.name + ": Failed to remove ladder pair from tray: " + e,
                );
              }
            }
          }
        } else {
          let pair = this.orderPairNameMap.get(rowId);
          let ladderPair: LadderPair = this.ladderPairMap.get(pair);
          if (ladderPair) {
            ladderPair.removeCurrentOrder(rowId);
            transaction.update.push(ladderPair);
            transaction.update.push(ladderPair.associatedRate);
          }
        }
      }
      this.orderPairNameMap.delete(rowId);

      if (this.hasBatchUpdateSubscription) {
        this.batchTransactionSubject.next({ transaction: transaction });
      }
    }

    const limitOrder = this._rawData[rowId];
    if (limitOrder != null) {
      const orderId = limitOrder.orderId.toString();

      const { aggregateId } = this.getAggregateId(limitOrder);
      const { context, transaction } = this.getContext();
      this.removeLimitOrderFromAggregate(orderId, aggregateId, context, transaction, true);

      if (this.hasBatchUpdateSubscription) {
        context.remove(orderId);
        this.batchTransactionSubject.next({
          transaction: context.convert(transaction, false, orderId),
        });
      }
      logger.out.info(`${this.constructor.name}.onDeleteEvent(${rowId})`);
    } else {
      logger.out.info(`${this.constructor.name}.onDeleteEvent(${rowId}) not found`);
    }

    delete this._rawData[rowId];
  }

  protected addOrUpdateRow(row: CubiqTS.OrderDataImpl): any {
    const orderId: string = row?.orderId?.toString?.();
    const isAdd = Object.hasOwn(this._rawData, orderId) === false;
    const limitOrder: LimitOrder = super.addOrUpdateRow(row);

    if (limitOrder == null) {
      // acceptsRow() check failed don't ever add to batch transaction
      return null;
    }

    let existingPair: string = this.orderPairNameMap.get(orderId);
    let pairName: string = limitOrder.baseCode + "/" + limitOrder.nonBaseCode;

    if (pairName) {
      // update maps
      if (existingPair && existingPair != pairName) {
        let orders = this.ordersByPair.get(existingPair);
        orders.delete(orderId);
      }
      let pairOrders: Set<string> = this.ordersByPair.get(pairName);
      if (!pairOrders) {
        pairOrders = new Set([orderId]);
        this.ordersByPair.set(pairName, pairOrders);
      } else {
        if (!pairOrders.has(orderId)) {
          pairOrders.add(orderId);
        }
      }
      this.orderPairNameMap.set(orderId, pairName);

      // add/update market bar + divider
      let invertName = row.nonBaseCode + "/" + row.baseCode + "&nbsp;¹/ₙ";
      if (!this._rawData[pairName]) {
        let transaction = { add: [], update: [], delete: [] };
        let ladderRate: LadderRate = new LadderRate();
        ladderRate.baseCode = row.baseCode;
        ladderRate.nonBaseCode = row.nonBaseCode;
        ladderRate[this._tableDefinition.primaryKey] = pairName;
        ladderRate[COLUMN_ORDER_BASE_TRACKING] = "- / -";
        ladderRate[COLUMN_INVERT_SPOT_TRACKING] = "- / -";
        ladderRate[COLUMN_ORDER_NDF_BASE_TRACKING] = "- / -";
        ladderRate[COLUMN_INVERT_NDF_TRACKING] = "- / -";
        ladderRate.selection = pairName;
        ladderRate.pairName = pairName;
        ladderRate.invertPairName = invertName;
        this._rawData[pairName] = ladderRate;
        transaction.add.push(ladderRate);
        let divider = new LadderPair();
        divider.baseCode = ladderRate.baseCode;
        divider.nonBaseCode = ladderRate.nonBaseCode;
        divider[this._tableDefinition.primaryKey] = pairName + "_div";
        divider.selection = pairName;
        if (this.pairIsInvertMap.get(pairName)) {
          ladderRate.isInvert = true;
          divider.isInvert = true;
        }
        divider.associatedRate = ladderRate;
        this.ladderPairMap.set(pairName, divider);
        this._rawData[divider[this._tableDefinition.primaryKey]] = divider;
        transaction.add.push(divider);
        this.ladderPairTrayProvider.push(divider);
        this.ladderPairTrayProvider.sort((a, b) => a.selection.localeCompare(b.selection));

        // map may have already been set by restoring from config
        if (!this.pairIsInvertMap.has(pairName)) {
          this.pairIsInvertMap.set(pairName, false);
        }

        if (this.hasBatchUpdateSubscription) {
          this.batchTransactionSubject.next({
            transaction: {
              add: [ladderRate, divider],
              update: [],
              remove: [],
            },
          });
        }

        let rate = this.rateCache.getRate(row.baseCode + row.nonBaseCode);
        if (rate) {
          // this will directly trigger an update to ladderRate
          this.processRates([rate]);
        }
      } else {
        this.updatePairTemperature(pairName);
      }

      if (this.addOrUpdateAggregate(limitOrder, orderId, pairName, isAdd) == null) {
        // NDF Outright - not aggregate eligible but still appears on ladder
        if (this.hasBatchUpdateSubscription) {
          this.batchTransactionSubject.next({
            transaction: {
              add: isAdd ? [limitOrder] : [],
              update: !isAdd ? [limitOrder] : [],
              remove: [],
            },
          });
        }
      }
    }

    return limitOrder;
  }

  private addOrUpdateAggregate(
    limitOrder: LimitOrder,
    orderId: string,
    rowPairName: string,
    isAdd: boolean,
  ): LadderAggregate | null {
    let aggregate: LadderAggregate;
    let isValid =
      limitOrder !== null &&
      limitOrder instanceof LimitOrder &&
      !AGGREGATE_DELETED_STATES.has(limitOrder.orderState) &&
      limitOrder.requestedOrderType !== CubiqTS.RequestOrderType.TSL &&
      limitOrder.requestedOrderType !== CubiqTS.RequestOrderType.TSL_BID &&
      limitOrder.requestedOrderType !== CubiqTS.RequestOrderType.TSL_ASK &&
      limitOrder.requestTrackingType !== CubiqTS.RequestTrackingType.OUTRIGHT_RATE &&
      (limitOrder.orderType === CubiqTS.OrderType.LIMIT_TP ||
        limitOrder.orderType === CubiqTS.OrderType.LIMIT_SL ||
        limitOrder.orderType === CubiqTS.OrderType.LIMIT_CALL);

    if (isValid) {
      const { aggregateId, aggregationTrackingRate } = this.getAggregateId(limitOrder);
      const { context, transaction } = this.getContext();
      this.removeLimitOrderFromAggregate(orderId, aggregateId, context, transaction, false);

      aggregate = this._rawData[aggregateId];

      if (aggregate) {
        aggregate.addUpdateLimitOrder(orderId, limitOrder, context);
        transaction.update.set(aggregateId, aggregate);
        this.orderIdAggregateIdMap.set(orderId, aggregateId);
      } else {
        aggregate = new LadderAggregate(
          aggregateId,
          aggregationTrackingRate,
          this.tableDefinitionFactory
            .getAggregationPricingService()
            .getInvertSpotTracking(limitOrder),
          limitOrder.baseCode,
          limitOrder.nonBaseCode,
          limitOrder.fixedCode,
          limitOrder.product,
          limitOrder.requestedOrderType,
          limitOrder.clientIntent,
          false,
          limitOrder.deskId,
          limitOrder.orderType,
          limitOrder.orderState,
          limitOrder.pauseState,
          limitOrder.trackingSide,
        );
        aggregate.addUpdateLimitOrder(orderId, limitOrder, context);
        this.orderPairNameMap.set(aggregateId, rowPairName);
        this._rawData[aggregateId] = aggregate;
        this.orderIdAggregateIdMap.set(orderId, aggregateId);
        transaction.add.set(aggregateId, aggregate);
      }

      if (this.hasBatchUpdateSubscription) {
        this.batchTransactionSubject.next({
          transaction: context.convert(transaction, isAdd, orderId),
        });
      }

      return aggregate;
    } else if (this.orderIdAggregateIdMap.has(orderId)) {
      const { context, transaction } = this.getContext();
      const aggregateId = this.orderIdAggregateIdMap.get(orderId);
      aggregate = this._rawData[aggregateId];
      aggregate.removeLimitOrder(orderId, context);
      this.orderIdAggregateIdMap.delete(orderId);

      if (aggregate.isEmpty()) {
        delete this._rawData[aggregateId];
        transaction.remove.set(aggregateId, aggregate);
      } else {
        transaction.update.set(aggregateId, aggregate);
      }

      if (this.hasBatchUpdateSubscription) {
        this.batchTransactionSubject.next({
          transaction: context.convert(transaction, isAdd, orderId),
        });
      }
      return aggregate;
    }

    return null;
  }

  private removeLimitOrderFromAggregate(
    orderId: string,
    aggregateId: string,
    context: AggregateServiceContext,
    transaction: { add: Map<any, any>; update: Map<any, any>; remove: Map<any, any> },
    removeForDelete: boolean,
  ) {
    let limitMoved = false;
    let oldAggregateId: string;
    let oldAggregate: LadderAggregate = null;

    if (this.orderIdAggregateIdMap.has(orderId)) {
      oldAggregateId = this.orderIdAggregateIdMap.get(orderId);
      limitMoved = oldAggregateId !== aggregateId;

      if (!removeForDelete && !limitMoved) {
        return;
      }

      oldAggregate = this._rawData[oldAggregateId];
      oldAggregate.removeLimitOrder(orderId, context);
      this.orderIdAggregateIdMap.delete(orderId);

      if (oldAggregate.isEmpty()) {
        delete this._rawData[oldAggregateId];
        transaction.remove.set(oldAggregateId, oldAggregate);
        return true;
      } else {
        transaction.update.set(oldAggregateId, oldAggregate);
        return false;
      }
    }
  }

  private getAggregateId(limitOrder: LimitOrder) {
    let aggregateIntent =
      limitOrder.baseCode != limitOrder.fixedCode
        ? 1 ^ limitOrder.clientIntent
        : limitOrder.clientIntent;
    let aggregationTrackingRate = this.tableDefinitionFactory
      .getAggregationPricingService()
      .getBlotterSpotTracking(limitOrder);
    let pauseState = limitOrder.pauseState ?? CubiqTS.PauseState.NOT_PAUSED;

    return {
      aggregateId: `${limitOrder.deskId.toString()}${limitOrder.orderType.toFixed(
        0,
      )}${pauseState.toFixed(0)}${limitOrder.product.toFixed(0)}${limitOrder.orderState.toFixed(
        0,
      )}${aggregateIntent.toFixed(0)}${limitOrder.baseCode}${
        limitOrder.nonBaseCode
      }${aggregationTrackingRate}`,
      aggregationTrackingRate: aggregationTrackingRate,
    };
  }

  private getContext() {
    let transaction = {
      add: new Map(),
      update: new Map(),
      remove: new Map(),
    };

    let context: AggregateServiceContext = {
      has: (id: string) => Object.hasOwn(this._rawData, id),
      get: (id: string) => this._rawData[id],
      add: (id: string) => {
        transaction.add.set(id, this._rawData[id]);
      },
      update: (id: string) => {
        transaction.update.set(id, this._rawData[id]);
      },
      remove: (id: string) => {
        const data = this._rawData[id];
        if (data != null) {
          delete this._rawData[id];
          transaction.remove.set(id, data);
        }
      },
      convert: (transaction, isAdd: boolean, id: string) => {
        if (isAdd) {
          let limitOrder = transaction.update.get(id);
          transaction.update.delete(id);
          transaction.add.set(id, limitOrder);
        }

        return {
          add: [...transaction.add.values()],
          update: [...transaction.update.values()],
          remove: [...transaction.remove.values()],
        };
      },
    };
    return { context, transaction };
  }

  public getPairIsInvertValues(): any {
    return JSON.stringify([...this.pairIsInvertMap].filter(([_, isInvert]) => isInvert === true));
  }

  public onInitialPrime(rows: any): any {
    return super.onInitialPrime(rows);
  }

  public isPairInverted(currencyPair: string): boolean {
    return this.pairIsInvertMap.get(currencyPair);
  }

  public restorePairIsInvertValues(json: string): void {
    let pairs: [pairName: string, isInvert: boolean][] = [];
    try {
      pairs = JSON.parse(json);
    } catch (e) {
      console.log(
        `${this.constructor.name}.restorePairIsInvertValue() failed to decode json: ${json}`,
      );
    }

    for (const [pairName, isInvert] of pairs) {
      if (isInvert && !this.pairIsInvertMap.has(pairName)) {
        this.pairIsInvertMap.set(pairName, isInvert);
      }
    }
  }

  /**
   * @param pair in the form base/nonBase_div
   * @returns LadderRate
   */
  public invertPair(pair: string): LadderRate {
    let ladderPair: LadderPair = this._rawData[pair];
    let ladderRate: LadderRate = ladderPair.associatedRate;
    let currentIsInvert: boolean = this.pairIsInvertMap.get(ladderRate.pairName);
    this.pairIsInvertMap.set(ladderRate.pairName, !currentIsInvert);
    let isInvert: boolean = this.pairIsInvertMap.get(ladderRate.pairName);
    ladderPair.isInvert = isInvert;
    this.updatePairProperties(ladderPair);
    return ladderRate;
  }

  private updatePairProperties(ladderPair: LadderPair) {
    let updatedOrders: TableModelObject[] = [];
    let ladderRate: LadderRate = ladderPair.associatedRate;
    if (ladderRate) {
      this.updateLadderRateProperties(ladderRate);
      updatedOrders.push(ladderRate);
    }

    if (ladderPair) {
      for (let id of ladderPair.currentOrders) {
        let order = this._rawData[id];
        if (LadderAggregate.is(order)) {
          let wasInvert = order.isLadderPairInvert;
          order.isLadderPairInvert = ladderPair.isInvert;
          if (wasInvert !== ladderPair.isInvert) {
            order.resetAggregateValues();
          }
        }
        updatedOrders.push(order);
      }
      updatedOrders.push(ladderPair);
    }
    if (updatedOrders.length > 0) {
      this.invertPairSubject.next(updatedOrders);
    }
  }

  private updateLadderRateProperties(ladderRate: LadderRate): void {
    let isInvert: boolean = this.pairIsInvertMap.get(ladderRate.pairName);
    ladderRate.isInvert = isInvert;
    if (isInvert) {
      ladderRate.selection = ladderRate.invertPairName;
    } else {
      ladderRate.selection = ladderRate.pairName;
    }
  }

  // private getInvertButton():string{
  // 	return '<button class="ui-button ui-markup-retrieve" (click)="retrieveMarkup($event)" tabindex="-1" (focus)="this.inputFocused = true" (mousedown)="this.inputFocused = true" [disabled]="input_disabled || data.disabled || locked || !this.data.retrieveMarkupEnabled" [ngClass]="{'disabled': input_disabled || data.disabled || locked,'btn-inactive': data.state === 3 && showValidationBar && !locked}"><span class="ui-clickable fa icon-refresh"></span></button>';
  // }

  public getLadderPairCount(): number {
    return this.ladderPairMap.size;
  }
}
